/**
 * Data Visualization - Intro Module Challenge Configuration
 *
 * 5 questions introducing the 5 core chart types:
 * Level 1 - Single-Table Basics  : Bar, Line, Pie    (Q1-Q3)
 * Level 2 - Multi-Dimension Views: Stacked Bar, Area (Q4-Q5)
 *
 * Dataset: Video Streaming Platform
 *   users        : user_id, signup_date, country, device_type
 *   watch_events : event_id, user_id, watch_date, watch_minutes, content_category
 */

export const DATA_VIZ_LEVELS = [
    {
        id: 1,
        title: 'Single-Table Basics',
        description: 'Bar, Line, and Pie charts - visualise one table, one dimension at a time',
        color: '#10b981',
    },
    {
        id: 2,
        title: 'Multi-Dimension Views',
        description: 'Stacked bar and area charts for comparing two dimensions simultaneously',
        color: '#3b82f6',
    },
];

export const DATA_VIZ_CHALLENGES = [

    // ═══ Level 1 - Single-Table Basics ══════════════════════════════

    {
        id: 'bar-chart-users-by-country',
        level: 1,
        title: 'Which countries have the most users?',
        businessContext: `You've just joined the analytics team at StreamPulse, a growing video streaming startup. The CEO asks: **"Where are our users coming from?"** Before diving into engagement or revenue, the first step is always to understand the audience. Let's build a simple bar chart that answers this question at a glance.`,
        question: 'Create a bar chart showing total users by country.',
        goal: 'Bar chart with countries on the X-axis and user count on the Y-axis.',
        hint: `A bar chart is perfect for comparing a count across categories.
1. Select the **users** table.
2. Set Metric to **COUNT** of \`user_id\`.
3. Add a Group By for **country**.
4. Run the query and select the **Bar** chart type.`,
        teach: 'Bar charts are the go-to for comparing a single numeric value across distinct categories. Each bar represents one category - in this case, one country - making it easy to rank and compare at a glance.',
        explanation: 'The bar chart gives us an immediate view of where our user base is concentrated, helping prioritise where to invest in marketing or localisation.',
        expectedChartType: 'bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'country' }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'line-chart-signup-trend',
        level: 1,
        title: 'How have signups trended over time?',
        businessContext: `You know where your users are. Now the growth team wants to know **when they signed up** - is growth accelerating, steady, or slowing down? Plotting signups over time reveals the shape of your growth curve and helps you connect changes to real-world events like campaigns or product launches.`,
        question: 'Create a line chart showing the number of new user signups per month.',
        goal: 'Line chart with month on the X-axis and signup count on the Y-axis.',
        hint: `A line chart shows change over time - set the X-axis to a date, grouped by month.
1. Select the **users** table.
2. Set Metric to **COUNT** of \`user_id\`.
3. Add a Group By for **signup_date** and set granularity to **Month**.
4. Run the query and select the **Line** chart type.`,
        teach: 'Line charts are ideal for time-series data because the connected line makes trends and inflection points easy to spot. Always use a date column on the X-axis and group by a meaningful granularity (day, week, month) to avoid a noisy chart.',
        explanation: 'Viewing signups at monthly granularity smooths out day-to-day noise and reveals the underlying growth trend - peaks and troughs often correspond to marketing pushes or product releases.',
        expectedChartType: 'line',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: true, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'signup_date', granularity: 'month' }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'pie-chart-device-breakdown',
        level: 1,
        title: 'What devices are our users on?',
        businessContext: `The product team wants to know **what proportion of users are on mobile vs desktop vs tablet**. This shapes decisions about which platform to prioritise for new features and bug fixes. A pie chart is perfect here - we care about the relative share, not the absolute number.`,
        question: 'Create a pie chart showing the distribution of users by device type.',
        goal: 'Pie chart with each slice representing a device type.',
        hint: `A pie chart shows proportional composition - perfect when you want "what fraction is X?" rather than tracking change.
1. Select the **users** table.
2. Set Metric to **COUNT** of \`user_id\`.
3. Add a Group By for **device_type**.
4. Run the query and select the **Pie** chart type.`,
        teach: 'Pie charts communicate part-to-whole relationships. They work best with a small number of categories (3-6) and when you want to emphasise proportional share. Use a bar chart instead if you need to compare exact values.',
        explanation: 'The pie chart immediately shows which platform dominates our user base, informing where engineering effort will have the most impact.',
        expectedChartType: 'pie',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'device_type' }],
            filters: [],
            join: null,
        },
    },

    // ═══ Level 2 - Multi-Dimension Views ════════════════════════════

    {
        id: 'stacked-bar-device-by-country',
        level: 2,
        title: 'How does device usage vary by country?',
        businessContext: `We know global device splits - but do all countries behave the same way? **Maybe mobile dominates in some markets while desktop leads in others.** A stacked bar chart lets us see both the total user count per country and the device composition within each country in a single view.`,
        question: 'Create a stacked bar chart showing user count by country, broken down by device type.',
        goal: 'Stacked bar chart - country on X-axis, bars split by device type.',
        hint: `Adding a second Group By dimension automatically creates a stacked / multi-series chart.
1. Select the **users** table.
2. Set Metric to **COUNT** of \`user_id\`.
3. Add a Group By for **country** and a second Group By for **device_type**.
4. Run the query and select the **Stacked Bar** chart type.`,
        teach: 'Stacked bar charts show two things at once: the total height of each bar (total count per category) and the coloured segments (composition within that category). They\'re ideal when you want to compare both volume and breakdown simultaneously.',
        explanation: 'This chart reveals not just which countries have the most users, but also which platform is dominant in each market - critical information for market-specific product decisions.',
        expectedChartType: 'stacked-bar',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: false, limit: false,
        },
        answer: {
            table: 'users',
            aggregation: { fn: 'COUNT', column: 'user_id' },
            groupBy: [{ column: 'country' }, { column: 'device_type' }],
            filters: [],
            join: null,
        },
    },

    {
        id: 'area-chart-watchtime-growth',
        level: 2,
        title: 'How has watch time grown across content categories?',
        businessContext: `The content team wants to understand **how viewership has evolved over time for each content category** - Drama, Sport, Documentary, etc. An area chart stacks the categories on top of each other, showing both the growth of each category and the overall platform growth in one view.`,
        question: 'Create an area chart showing total watch minutes per month, broken down by content category.',
        goal: 'Stacked area chart - month on X-axis, watch minutes stacked by content_category.',
        hint: `Join \`watch_events\` to get time-series watch data, then break by content category.
1. Select the **watch_events** table.
2. Set Metric to **SUM** of \`watch_minutes\`.
3. Add a Group By for **watch_date** (granularity: **Month**) and a second Group By for **content_category**.
4. Run the query and select the **Area** chart type.`,
        teach: 'Area charts are a variation of line charts where the space below the line is filled in. When multiple series are stacked, the total height shows overall volume and the layers show how each category contributes. They\'re great for visualising growth with composition over time.',
        explanation: 'The stacked area chart shows how total watch time has grown month over month and which content categories are driving that growth - helping the content team know where to invest next.',
        expectedChartType: 'area',
        availableControls: {
            table: true, aggregation: true, groupBy: true,
            filters: false, join: false, granularity: true, limit: false,
        },
        answer: {
            table: 'watch_events',
            aggregation: { fn: 'SUM', column: 'watch_minutes' },
            groupBy: [{ column: 'watch_date', granularity: 'month' }, { column: 'content_category' }],
            filters: [],
            join: null,
        },
    },
];

/**
 * Validate user's query state against the expected answer.
 * Checks every dimension: table, join, filters, chart type,
 * aggregation (fn + column), groupBy (cols + granularity), and limit.
 * Returns { passed: boolean, feedback: string }
 */
export function validateAnswer(userQuery, challenge) {
    const { answer, expectedChartType } = challenge;

    const typeNames = {
        bar: 'Bar', 'stacked-bar': 'Stacked Bar', line: 'Line',
        area: 'Area', pie: 'Pie', histogram: 'Histogram',
        heatmap: 'Heatmap', map: 'Map', boxplot: 'Box Plot', scatter: 'Scatter',
    };

    // ── 1. Table ──────────────────────────────────────────────────
    if (userQuery.table !== answer.table) {
        return { passed: false, feedback: `Wrong table. Select the \`${answer.table}\` table.` };
    }

    // ── 2. Join ───────────────────────────────────────────────────
    const userJoinTable = userQuery.join?.table || null;
    const answerJoinTable = answer.join?.table || null;
    if (answerJoinTable && !userJoinTable) {
        return { passed: false, feedback: `This question requires a join - add the \`${answerJoinTable}\` table.` };
    }
    if (answerJoinTable && userJoinTable !== answerJoinTable) {
        return { passed: false, feedback: `Wrong join table. Join \`${answerJoinTable}\`, not \`${userJoinTable}\`.` };
    }
    if (!answerJoinTable && userJoinTable) {
        return { passed: false, feedback: `Remove the join - this question only uses the \`${answer.table}\` table.` };
    }

    // ── 3. Filters ────────────────────────────────────────────────
    const activeUserFilters = (userQuery.filters || []).filter(f => f.column && f.value !== '' && f.value != null);
    const activeAnswerFilters = (answer.filters || []).filter(f => f.column && f.value !== '' && f.value != null);
    if (activeUserFilters.length > 0 && activeAnswerFilters.length === 0) {
        const extra = activeUserFilters[0];
        return { passed: false, feedback: `Remove the filter on \`${extra.column}\` - this question doesn't need any filters.` };
    }
    if (activeAnswerFilters.length > 0) {
        for (const af of activeAnswerFilters) {
            const match = activeUserFilters.find(f => f.column === af.column && String(f.value) === String(af.value));
            if (!match) {
                return { passed: false, feedback: `Add a filter: \`${af.column}\` = \`${af.value}\`.` };
            }
        }
        const extra = activeUserFilters.find(f => !activeAnswerFilters.some(af => af.column === f.column));
        if (extra) {
            return { passed: false, feedback: `Remove the extra filter on \`${extra.column}\`.` };
        }
    }

    // ── 4. Chart type ─────────────────────────────────────────────
    if (userQuery.chartType !== expectedChartType) {
        return { passed: false, feedback: `Wrong chart type. Select the **${typeNames[expectedChartType] || expectedChartType}** chart.` };
    }

    // ── 5. Aggregation (fn + column, no extra metrics) ────────────
    if (answer.aggregation) {
        const userMetrics = userQuery.metrics || [];
        if (userMetrics.length === 0) {
            return { passed: false, feedback: `Set the metric to **${answer.aggregation.fn}** of \`${answer.aggregation.column}\`.` };
        }
        if (userMetrics.length > 1) {
            return { passed: false, feedback: `Only one metric is needed - remove the extra one.` };
        }
        const m = userMetrics[0];
        if (m.fn !== answer.aggregation.fn) {
            return { passed: false, feedback: `Wrong aggregation function. Use **${answer.aggregation.fn}**, not **${m.fn}**.` };
        }
        if (m.column !== answer.aggregation.column) {
            return { passed: false, feedback: `Wrong metric column. Aggregate on \`${answer.aggregation.column}\`, not \`${m.column}\`.` };
        }
    }

    // ── 6. GroupBy (missing, extra, and granularity) ──────────────
    const userGroupBy = userQuery.groupBy || [];
    const answerGroupBy = answer.groupBy || [];
    if (answerGroupBy.length === 0 && userGroupBy.length > 0) {
        return { passed: false, feedback: `Remove all Group Bys - this question doesn't need any.` };
    }
    if (answerGroupBy.length > 0) {
        const userGroupCols = userGroupBy.map(g => g.column);
        const requiredCols = answerGroupBy.map(g => g.column);
        const missing = requiredCols.filter(c => !userGroupCols.includes(c));
        if (missing.length > 0) {
            return { passed: false, feedback: `Missing group by: add **${missing.join('** and **')}** to your Group By.` };
        }
        const extra = userGroupCols.filter(c => !requiredCols.includes(c));
        if (extra.length > 0) {
            return { passed: false, feedback: `Remove **${extra.join('** and **')}** from your Group By - it's not needed here.` };
        }
        // Check granularity on each groupBy that requires it
        for (const ag of answerGroupBy) {
            if (ag.granularity) {
                const userG = userGroupBy.find(g => g.column === ag.column);
                if (!userG?.granularity) {
                    return { passed: false, feedback: `Set the granularity for **${ag.column}** to **${ag.granularity}**.` };
                }
                if (userG.granularity.toLowerCase() !== ag.granularity.toLowerCase()) {
                    return { passed: false, feedback: `Wrong granularity for **${ag.column}**: use **${ag.granularity}**, not **${userG.granularity}**.` };
                }
            }
        }
    }

    // ── 7. Limit ──────────────────────────────────────────────────
    const userLimit = userQuery.limit ?? null;
    const answerLimit = answer.limit ?? null;
    if (!answerLimit && userLimit) {
        return { passed: false, feedback: `Remove the row limit - this question doesn't need one.` };
    }
    if (answerLimit && userLimit !== answerLimit) {
        return { passed: false, feedback: `Set the row limit to **${answerLimit}**.` };
    }

    return { passed: true, feedback: '✅ Great work! You built the right visualisation.' };
}
